import { FC } from 'react';

import { MapPin, Package, Truck, User } from 'lucide-react';

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import { Separator } from '../../components/ui/Separator';
import { Order } from '../../types';

const METHOD_CONFIG: Record<string, { label: string; icon: typeof Truck }> = {
    nova_poshta: { label: 'Нова Пошта (Відділення)', icon: Truck },
    ukrposhta: { label: 'Укрпошта', icon: Package },
    pickup: { label: 'Самовивіз', icon: MapPin },
};

interface Props {
    order: Order;
    index: number;
    isMultipleOrders: boolean;
    getOrderNumber: (order: Order) => string | number;
    getItemsCount: (order: Order) => number;
    getFinalTotal: (order: Order) => number;
}

export const OrderPlacedDetailsCard: FC<Props> = ({
    order,
    index,
    isMultipleOrders,
    getOrderNumber,
    getItemsCount,
    getFinalTotal,
}) => {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {isMultipleOrders
                        ? `Замовлення ${index + 1} - ${getOrderNumber(order)}`
                        : `Деталі замовлення`}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {order.seller && (
                        <div className="mb-2 flex items-center gap-2 text-sm text-gray-600 dark:text-foreground/60">
                            <User className="h-4 w-4" />
                            <span>
                                Продавець:{' '}
                                <span className="font-medium text-gray-900 dark:text-foreground">
                                    {order.seller.nickname}
                                </span>
                            </span>
                        </div>
                    )}
                    <Separator />
                    <div className="space-y-2">
                        {order.order_items && order.order_items.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-foreground">
                                    Товари:
                                </p>
                                {order.order_items.map((item) => (
                                    <div
                                        key={item.id}
                                        className="flex justify-between text-sm"
                                    >
                                        <span className="text-gray-600 dark:text-foreground/60">
                                            {item.product?.name || 'Товар'}
                                            {item.amount > 1 &&
                                                ` x${item.amount}`}
                                        </span>
                                        <span className="font-medium">
                                            ₴{item.price_per_item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <Separator />
                        {order.delivery && (
                            <div className="space-y-3 py-1">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-foreground">
                                    Доставка:
                                    {(() => {
                                        const config =
                                            METHOD_CONFIG[
                                                order.delivery.method
                                            ];
                                        const MethodIcon = config?.icon;
                                        return MethodIcon ? (
                                            <MethodIcon className="h-4 w-4 text-primary" />
                                        ) : null;
                                    })()}
                                    <span>
                                        {METHOD_CONFIG[order.delivery.method]
                                            ?.label ??
                                            order.delivery.method_label}
                                    </span>
                                </div>
                                {(order.delivery.recipient_name ||
                                    order.delivery.recipient_phone) && (
                                    <div className="space-y-0.5 text-sm text-gray-600 dark:text-foreground/60">
                                        {order.delivery.recipient_name && (
                                            <p>
                                                {order.delivery.recipient_name}
                                            </p>
                                        )}
                                        {order.delivery.recipient_phone && (
                                            <p>
                                                {order.delivery.recipient_phone}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {order.delivery.city && (
                                    <p className="text-sm text-gray-600 dark:text-foreground/60">
                                        {order.delivery.city}
                                        {order.delivery.region
                                            ? `, ${order.delivery.region}`
                                            : ''}
                                    </p>
                                )}
                                {order.delivery.method === 'nova_poshta' &&
                                    order.delivery.delivery_point_number && (
                                        <p className="text-sm text-gray-600 dark:text-foreground/60">
                                            {order.delivery
                                                .delivery_point_type ===
                                            'parcel_locker'
                                                ? 'Поштомат'
                                                : 'Відділення'}{' '}
                                            №
                                            {
                                                order.delivery
                                                    .delivery_point_number
                                            }
                                        </p>
                                    )}
                                {order.delivery.method === 'ukrposhta' &&
                                    order.delivery.post_office && (
                                        <p className="text-sm text-gray-600 dark:text-foreground/60">
                                            {order.delivery.post_office}
                                        </p>
                                    )}
                                {order.delivery.method === 'pickup' && (
                                    <p className="text-sm text-gray-600 dark:text-foreground/60">
                                        Забрати особисто у продавця
                                    </p>
                                )}
                                {order.delivery.comment_for_seller && (
                                    <p className="text-sm text-gray-500 italic dark:text-foreground/50">
                                        Коментар:{' '}
                                        {order.delivery.comment_for_seller}
                                    </p>
                                )}
                            </div>
                        )}
                        <Separator />
                        <div className="space-y-2 pt-2">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600 dark:text-foreground/60">
                                    Кількість товарів:
                                </span>
                                <span className="font-medium">
                                    {getItemsCount(order)} шт.
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="font-bold">
                                    Загальна сума:
                                </span>
                                <span className="text-lg font-bold">
                                    ₴{getFinalTotal(order).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
